/* eslint-disable max-statements */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
    ADMIN_SECURITY_GROUP,
    AGENTROLE,
    CAMENU,
    CATEMPLATES,
    COACHING,
    COMPENSATION,
    COMPENSATION_SECURITY_GROUP,
    DEV_APP_ENVIRONMENT,
    EDSDASHBOARD,
    EDS_SECURITY_GROUP,
    HC_SECURITY_GROUP,
    LANDINGPAGE,
    MANAGERDASHBOARD,
    MANAGERROLE,
    PRODUCTION_APP_ENVIRONMENT,
    ROLES,
    SUPERVISORROLE,
    UNAUTHORIZED,
} from '../../constants';
import { Login } from '../../features/login/Login';
import { useGetUserUserActivityCountQuery } from '../../features/moodMeter/MoodMeterSlice';
import { useAddUserActivityLogMutation } from '../metricdashboard/metricDashboardSlice';
import './AuthLogin.scss';
import {
    selectCurrentIsAdmin,
    selectCurrentIsEds,
    selectCurrentIsHC,
    selectCurrentRole,
    selectCurrentUser,
    selectCurrentisComp,
    selectIsDelegate,
    selectIsManagerRecentApprover,
    setActualLoggedIn,
    setAuthCredentials,
    setCredentials,
    setImpersonateCredentials,
    setIsManagerRecentApprover,
    setUserActiveCount,
} from './authSlice';
import { checkHeartbeat, IDecodedIdToken, sendAuthorizationRequest, sendTokenRequest } from './authToken';
import { useGetManagerRecentApprovalsCountQuery } from '../managerDashboard/managerDashboardSlice';

// ── Constants ──────────────────────────────────────────────────────────────────

const USER_TYPE = {
    Admin: 'Admin',
    Eds: 'Eds',
    Comp: 'Comp',
    HC: 'HC',
} as const;

type UserType = typeof USER_TYPE[keyof typeof USER_TYPE];

const LOGIN_DESCRIPTION = 'Login';

// ── Pure helpers ───────────────────────────────────────────────────────────────

// Accepts IDecodedIdToken directly — it already contains memberOf: string[]
const checkUserType = (userInfo: IDecodedIdToken, typeCheck: UserType): boolean =>
    (userInfo.memberOf ?? []).some((group: string) => {
        const groupName = group?.split('CN=')[1];
        const securityGroupName = groupName?.substring(0, groupName.indexOf(','));

        switch (typeCheck) {
            case USER_TYPE.Admin:
                return securityGroupName === ADMIN_SECURITY_GROUP;
            case USER_TYPE.Eds:
                return securityGroupName === EDS_SECURITY_GROUP;
            case USER_TYPE.Comp:
                return securityGroupName === COMPENSATION_SECURITY_GROUP;
            case USER_TYPE.HC:
                return securityGroupName === HC_SECURITY_GROUP;
            default:
                return false;
        }
    });

interface RouteFlags {
    role: string;
    isAdmin: boolean;
    isEds: boolean;
    isComp: boolean;
    isHC: boolean;
    isDelegate: boolean;
    isRecentApprover: boolean;
    hasCaid: boolean;
}

const resolveRoute = (flags: RouteFlags): string => {
    const { role, isAdmin, isEds, isComp, isHC, isDelegate, isRecentApprover, hasCaid } = flags;

    if (hasCaid) return CAMENU;

    if ((role === AGENTROLE || role === SUPERVISORROLE) && !isEds && !isComp) {
        return LANDINGPAGE;
    }

    if (role === MANAGERROLE) {
        if (isRecentApprover) return LANDINGPAGE;
        return MANAGERDASHBOARD;
    }

    if (!isAdmin) {
        if (isEds && !isDelegate) return EDSDASHBOARD;
        if (isComp) return COMPENSATION;
        if (isHC) return CATEMPLATES;
        if (isDelegate) return COACHING;
    }

    return UNAUTHORIZED;
};

// ── Component ──────────────────────────────────────────────────────────────────

const AuthLogin = () => {
    const [AddUserActivity] = useAddUserActivityLogMutation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { caid } = useParams();

    // Persist caid to localStorage as a layout-time side effect (before paint)
    useLayoutEffect(() => {
        if (caid) {
            localStorage.setItem('caid', caid);
        }
    }, [caid]);

    const user = useSelector(selectCurrentUser);
    const role = useSelector(selectCurrentRole);
    const impEds = useSelector(selectCurrentIsEds);
    const impAdmin = useSelector(selectCurrentIsAdmin);
    const impComp = useSelector(selectCurrentisComp);
    const isImpIsHC = useSelector(selectCurrentIsHC);
    const isDelegate = useSelector(selectIsDelegate);
    const isUserRecentApprover = useSelector(selectIsManagerRecentApprover);

    const [isAdmin, setIsAdmin] = useState(false);
    const [isEds, setIsEds] = useState(false);
    const [isComp, setIsComp] = useState(false);
    const [employeeId, setEmployeeId] = useState('');
    // Local flag mirrors the Redux value but is set synchronously within the
    // token-exchange effect so navigateAfterActivity sees the correct value
    // without waiting for a Redux re-render cycle.
    const [isLoginUserRecentApprover, setIsLoginUserRecentApprover] = useState(false);

    const tokenRequestSent = useRef(false); // guard against React StrictMode double-invoke

    // ── Queries ──────────────────────────────────────────────────────────────

    const { data: userActiveCount } = useGetUserUserActivityCountQuery(
        { employeeNumber: employeeId },
        { skip: !employeeId },
    );

    const { data: employeeRecentApproverStatus } = useGetManagerRecentApprovalsCountQuery(
        { employeeNumber: employeeId },
        { skip: !employeeId || role !== MANAGERROLE },
    );

    // ── Navigation helper ─────────────────────────────────────────────────────

    const navigateAfterActivity = useCallback(
        (empId: string, currentRole: string, flags: Omit<RouteFlags, 'hasCaid' | 'role'>) => {
            const payload = {
                description: LOGIN_DESCRIPTION,
                employeenumber: empId,
            };

            AddUserActivity(payload)
                .unwrap()
                .then(() => {
                    const route = resolveRoute({
                        ...flags,
                        role: currentRole,
                        hasCaid: !!localStorage.getItem('caid'),
                    });
                    navigate(route, route === UNAUTHORIZED ? { replace: true } : undefined);
                })
                .catch((error: unknown) => {
                    console.log(error);
                });

            dispatch(setActualLoggedIn(empId));
        },
        [AddUserActivity, dispatch, navigate],
    );

    // ── Effects ───────────────────────────────────────────────────────────────

    // Intentional mount-only effect: redirects an already-authenticated user.
    // Deps are omitted deliberately — this must run exactly once on mount,
    // reading the Redux snapshot at that moment.
    useEffect(
        () => {
            const isLoggedIn =
                typeof user === 'string' &&
                user.trim().length > 0 &&
                typeof role === 'string' &&
                role.trim().length > 0;

            if (!isLoggedIn) return;

            const route = resolveRoute({
                role,
                isAdmin: impAdmin ?? false,
                isEds: impEds ?? false,
                isComp: impComp ?? false,
                isHC: isImpIsHC ?? false,
                isDelegate: isDelegate ?? false,
                isRecentApprover: isUserRecentApprover ?? false,
                hasCaid: false,
            });

            navigate(route, route === UNAUTHORIZED ? { replace: true } : undefined);
        },
        [] // mount-only — stale closure is intentional
    );

    // Intentional data-driven effect: fires when the activity-count query settles.
    // navigateAfterActivity is stable (useCallback) so omitting it here is safe;
    // including the full closure would re-run this on unrelated renders.
    useEffect(
        () => {
            dispatch(setUserActiveCount(userActiveCount?.data));

            if (userActiveCount?.status) {
                navigateAfterActivity(employeeId, role ?? '', {
                    isAdmin,
                    isEds,
                    isComp,
                    isHC: isImpIsHC ?? false,
                    isDelegate: isDelegate ?? false,
                    isRecentApprover: isLoginUserRecentApprover,
                });
            }
        },
        [userActiveCount] // data-driven — only re-run when query result changes
    );

    // Intentional data-driven effect: fires when the recent-approver query settles.
    useEffect(
        () => {
            if (!employeeRecentApproverStatus?.status) return;

            const isRecentApprover = employeeRecentApproverStatus.data === true;
            setIsLoginUserRecentApprover(isRecentApprover);
            dispatch(setIsManagerRecentApprover(isRecentApprover));

            if (isRecentApprover && role === MANAGERROLE) {
                navigate(LANDINGPAGE);
            }
        },
        [employeeRecentApproverStatus] // data-driven — only re-run when query result changes
    );
    useEffect(
        () => {
            checkHeartbeat().then((result) => {
                if (!result.success) {
                    console.warn('[Heartbeat] Connectivity issue detected at login :', {
                        errorCode: result.errorCode,
                        httpStatus: result.httpStatus,
                        latencyMs: result.latencyMs,
                        isNetworkBlock: result.isNetworkBlock,
                        timestamp: new Date().toISOString(),
                    });
                }
            });
        },
        []
    );

    // Intentional mount-only effect: exchanges the PingOne auth code for tokens.
    // tokenRequestSent ref prevents double-invocation under React StrictMode.
    useEffect(
        () => {
            // const hash = window.location.hash.substring(1);
            // const hashParams = new URLSearchParams(hash);
            // const code = hashParams.get('code');
             const code = new URL(window.location.href).searchParams.get('code');

            if (!code || tokenRequestSent.current) return;
            tokenRequestSent.current = true;

            sendTokenRequest(code)
                .then(([tokenData, userInfo]) => {

                    // console.log('TOKEN RESPONSE', tokenData, userInfo);
                    const empId = userInfo.oracleID.trim();
                    const firstName = userInfo.fname.trim();
                    const userFullName = `${userInfo.fname.trim()} ${userInfo.lname.trim()}`;
                    const userEmail=userInfo.email.trim();
                    let userManagerLevel = userInfo.userManagerLevel.trim();

                    // checkUserType accepts IDecodedIdToken — no cast needed
                    const userIsAdmin = checkUserType(userInfo, USER_TYPE.Admin);
                    const userIsEds = checkUserType(userInfo, USER_TYPE.Eds);
                    const userIsComp = checkUserType(userInfo, USER_TYPE.Comp);
                    const userIsHC = checkUserType(userInfo, USER_TYPE.HC);

                    setIsAdmin(userIsAdmin);
                    setIsEds(userIsEds);
                    setIsComp(userIsComp);

                    // Dev-only fallback: admin users without a manager level become agents
                    if (
                        userManagerLevel === undefined &&
                        process.env.REACT_APP_ENVIRONMENT === DEV_APP_ENVIRONMENT &&
                        userIsAdmin
                    ) {
                        userManagerLevel = 'Non-Manager';
                    }

                    let resolvedRole = '';
                    if (ROLES.Agents?.find((r) => userManagerLevel?.includes(r))) resolvedRole = AGENTROLE;
                    if (ROLES.Supervisor?.find((r) => userManagerLevel?.includes(r))) resolvedRole = SUPERVISORROLE;
                    if (ROLES.Manager?.find((r) => userManagerLevel?.includes(r))) resolvedRole = MANAGERROLE;

                    if (resolvedRole === '') {
                        navigate(UNAUTHORIZED, { replace: true });
                        return;
                    }

                    const idToken = tokenData.id_token;
                    const { refresh_token: refreshToken, expires_in: expiresIn } = tokenData;

                    const userObj = {
                        empid: empId,
                        user: firstName,
                        email: userEmail,
                        role: resolvedRole,
                        userManagerLevel,
                        isAdmin: userIsAdmin,
                        isEds: userIsEds,
                        isComp: userIsComp,
                        userFullName,
                        isHC: userIsHC,
                    };

                    dispatch(setCredentials(userObj));
                    dispatch(setAuthCredentials({ token: idToken, refreshToken, expiresIn }));
                    setEmployeeId(empId);

                    const isProduction = process.env.REACT_APP_ENVIRONMENT === PRODUCTION_APP_ENVIRONMENT;
                    if (isProduction ? userIsAdmin : true) {
                        dispatch(setImpersonateCredentials(userObj));
                    }
                })
                .catch((error: unknown) => {
                    console.log('TOKEN REQUEST ERROR', error);
                });
        },
        [] // mount-only — token exchange runs exactly once after callback redirect
    );

    // ── Memoised props ─────────────────────────────────────────────────────────

    const loginProps = useMemo(
        () => ({ sendAuthorizationRequest }),
        [],
    );

    return <Login {...loginProps} />;
};

export default AuthLogin;
